import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useLocation, useSearchParams } from "react-router-dom";
import { message, Modal } from "antd";
import communityAPI from "../../../api/community";
import { usePost, useComments, usePosts } from "../../../hooks/useCommunity";
import Avatar from "../../../componentes/ui/Avatar";
import verifiedBadge from "../../../assets/verified.png";
import Badge from "../../../componentes/ui/Badge";
import ReportButton from "../ReportButton";
import ReportModal from "../ReportModal";
import ShareModal from "../shared/ShareModal";
import ReviewFeedbackNotice from "../ReviewFeedbackNotice";
import LoginPrompt from "../shared/LoginPrompt";
import { useCommunityAuth } from "../../../contexts/CommunityAuthContext";
import { FullscreenSpinner, InlineSpinner } from '../../../componentes/LoadingIndicator';
import AuthorSpotlight from "../shared/AuthorSpotlight";
import MoreFromAuthor from "../shared/MoreFromAuthor";
import { applyFeedbackHighlights } from "../../../utils/feedbackHighlight";
import { formatDate } from "../../../utils/dateFormat";
import { formatNumber } from "../../../utils/numberFormat";
import { Heart, MessageCircle, Eye } from "lucide-react";
import { sanitizeRichText } from "../../../utils/sanitizeHtml";
import { truncateText } from "../../../utils/truncateText";

const resolveFollowerCount = (source, fallback = 0) => {
  if (!source) {
    return Math.max(0, fallback);
  }
  const numericKeys = ["followers_count", "followers_total", "followers"];
  for (const key of numericKeys) {
    if (typeof source[key] === "number") {
      return Math.max(0, source[key]);
    }
  }
  if (Array.isArray(source.followers)) {
    return source.followers.length;
  }
  return Math.max(0, fallback);
};

const resolveFollowingCount = (source, fallback = 0) => {
  if (!source) {
    return Math.max(0, fallback);
  }
  const numericKeys = ["following_count", "following_total", "following"];
  for (const key of numericKeys) {
    if (typeof source[key] === "number") {
      return Math.max(0, source[key]);
    }
  }
  if (Array.isArray(source.following)) {
    return source.following.length;
  }
  return Math.max(0, fallback);
};

export default function ForumDetail() {
  const { id: slug } = useParams();
  const { user } = useCommunityAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const {
    post: thread,
    loading: threadLoading,
    error: threadError,
    refetch: refetchThread,
  } = usePost(slug);

  const [liked, setLiked] = useState(false);
  const [localLikes, setLocalLikes] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [postReplyText, setPostReplyText] = useState("");
  const [commentReplyText, setCommentReplyText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [repliesToShow, setRepliesToShow] = useState(5);
  const [expandedComments, setExpandedComments] = useState({});
  const [visibleReplyCounts, setVisibleReplyCounts] = useState({});
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingComment, setReportingComment] = useState(null);
  const [submittingReport, setSubmittingReport] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(null);
  const [authorFollowMeta, setAuthorFollowMeta] = useState({
    isFollowing: false,
    followers: 0,
  });
  const [authorFollowLoading, setAuthorFollowLoading] = useState(false);
  const [authorProfile, setAuthorProfile] = useState(null);
  const fromAdmin = Boolean(location.state?.fromAdmin ?? searchParams.get("fromAdmin") === "1");
  const adminBackTo = location.state?.backTo || searchParams.get("backTo") || "/admin/content/forums";
  const fromReports = Boolean(location.state?.fromReports);

  const syncAuthorFollowMeta = useCallback((source) => {
    if (!source) {
      return;
    }
    setAuthorFollowMeta((prev) => {
      const nextFollowers = resolveFollowerCount(source, prev.followers);
      const nextIsFollowing =
        typeof source.is_following === "boolean"
          ? source.is_following
          : prev.isFollowing;

      if (
        nextFollowers === prev.followers &&
        nextIsFollowing === prev.isFollowing
      ) {
        return prev;
      }

      return {
        isFollowing: nextIsFollowing,
        followers: nextFollowers,
      };
    });
  }, []);

  const highlightedContent = useMemo(() => {
    if (!thread?.content) {
      return "";
    }
    const highlights = thread.review_feedback?.highlights;
    if (
      thread.is_approved ||
      !Array.isArray(highlights) ||
      highlights.length === 0
    ) {
      return thread.content;
    }
    return applyFeedbackHighlights(thread.content, highlights);
  }, [thread]);

  const sanitizedContent = useMemo(() => {
    let html = sanitizeRichText(highlightedContent) || "";
    // Replace non-breaking spaces with regular space so the browser can wrap lines
    // Also remove zero-width spaces/soft hyphens to prevent weird intra-word breaks
    html = html.replace(/(&nbsp;|\u00A0)/g, " ");
    html = html.replace(/[\u00AD\u200B\u200C\u200D\u200E\u200F\uFEFF]/g, "");
    return html;
  }, [highlightedContent]);

  const reviewStatus = thread?.review_status || "pending_review";
  const statusMeta = useMemo(() => {
    if (!thread || thread.is_approved) {
      return null;
    }
    if (reviewStatus === "requires_revision") {
      return { color: "orange", label: "Needs Your Revision" };
    }
    if (reviewStatus === "pending_review") {
      return { color: "yellow", label: "Awaiting Moderator Review" };
    }
    return null;
  }, [thread, reviewStatus]);
  const {
    comments,
    loading: commentsLoading,
    fetchComments: refetchComments,
    createComment,
    updateComment,
    deleteComment,
    toggleLike: toggleCommentLike,
  } = useComments(thread?.id, { enabled: Boolean(thread?.id) });

  const { posts: forumCollection, loading: forumCollectionLoading } = usePosts(
    { post_type: "forum", ordering: "-created_at" },
    { paginated: false, pageSize: 50, skip: !thread?.id }
  );

  const refreshThreadData = useCallback(async () => {
    await Promise.all([refetchThread(), refetchComments()]);
  }, [refetchThread, refetchComments]);

  useEffect(() => {
    if (threadError) {
      message.error("Thread not found");
    }
  }, [threadError]);

  useEffect(() => {
    if (!thread) {
      setLiked(false);
      setLocalLikes(null);
      setBookmarked(false);
      return;
    }
    const likes = thread.likes_total ?? thread.likes_count ?? 0;
    setLiked(Boolean(thread.user_has_liked));
    setLocalLikes(likes);
    setBookmarked(Boolean(thread.user_has_bookmarked));
  }, [thread]);



  useEffect(() => {
    setVisibleReplyCounts({});
    setRepliesToShow(5);
  }, [thread?.id]);

  useEffect(() => {
    syncAuthorFollowMeta(thread?.author);
  }, [
    thread?.author?.username,
    thread?.author?.followers_count,
    thread?.author?.is_following,
    syncAuthorFollowMeta,
  ]);

  useEffect(() => {
    setAuthorProfile(thread?.author || null);
  }, [thread?.author]);

  useEffect(() => {
    if (!thread?.author?.username) {
      return;
    }
    let cancelled = false;
    const loadFollowSnapshot = async () => {
      try {
        const { data } = await communityAPI.users.getByUsername(
          thread.author.username
        );
        if (!cancelled) {
          syncAuthorFollowMeta(data);
          setAuthorProfile(data);
        }
      } catch (err) {
        console.error('Failed to refresh author follow stats', err);
      }
    };
    loadFollowSnapshot();
    return () => {
      cancelled = true;
    };
  }, [thread?.author?.username, syncAuthorFollowMeta]);

  const threadLikes = useMemo(
    () => thread?.likes_total ?? thread?.likes_count ?? 0,
    [thread?.likes_total, thread?.likes_count]
  );

  const displayedLikes = localLikes ?? threadLikes;

  const resolvedAuthor = authorProfile || thread?.author || null;
  const authorUsername = resolvedAuthor?.username;
  const rawFirstName =
    resolvedAuthor?.first_name ||
    resolvedAuthor?.profile?.first_name ||
    "";
  const rawLastName =
    resolvedAuthor?.last_name ||
    resolvedAuthor?.profile?.last_name ||
    "";
  const calculatedFullName = [rawFirstName, rawLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const authorDisplayName =
    calculatedFullName ||
    resolvedAuthor?.name ||
    resolvedAuthor?.display_name ||
    authorUsername ||
    "Community Member";
  const authorHeadline = resolvedAuthor?.title || resolvedAuthor?.role || "";
  const rawAuthorBio = resolvedAuthor?.bio ?? resolvedAuthor?.profile?.bio;
  const authorBio = useMemo(() => {
    return typeof rawAuthorBio === "string" ? rawAuthorBio.trim() : "";
  }, [rawAuthorBio]);
  const authorFollowingCount = useMemo(
    () => resolveFollowingCount(resolvedAuthor),
    [resolvedAuthor?.following_count, resolvedAuthor?.following_total, resolvedAuthor?.following]
  );
  const authorProfileLink = authorUsername ? `/profile/${authorUsername}` : null;
  const authorJoinedDate =
    resolvedAuthor?.date_joined || resolvedAuthor?.profile?.date_joined || null;
  const authorAvatar =
    resolvedAuthor?.avatar_url ||
    resolvedAuthor?.avatar ||
    resolvedAuthor?.profile?.avatar_url ||
    resolvedAuthor?.profile?.avatar ||
    null;
  const rawAuthorSkills = resolvedAuthor?.skills || resolvedAuthor?.profile?.skills;
  const authorSkills = useMemo(() => {
    if (!Array.isArray(rawAuthorSkills)) {
      return [];
    }
    return rawAuthorSkills
      .map((skill) => (typeof skill === "string" ? skill.trim() : ""))
      .filter(Boolean)
      .slice(0, 6);
  }, [rawAuthorSkills]);

  const authorId = resolvedAuthor?.id ?? thread?.author?.id ?? null;
  const isThreadAuthor = Boolean(user && authorId && user.id === authorId);
  const showAuthorFollowCta = Boolean(authorUsername) && !isThreadAuthor;
  const threadFollowerLabel =
    authorFollowMeta.followers === 1 ? "Follower" : "Followers";

  const suggestionBase = useMemo(() => {
    if (!Array.isArray(forumCollection)) {
      return [];
    }
    const currentId = thread?.id;
    return forumCollection.filter((item) => item.id !== currentId);
  }, [forumCollection, thread?.id]);

  const featuredThreads = useMemo(() => {
    if (suggestionBase.length === 0) {
      return [];
    }
    return [...suggestionBase]
      .sort(
        (a, b) =>
          (b.comments_count ?? b.comments_total ?? 0) -
          (a.comments_count ?? a.comments_total ?? 0)
      )
      .slice(0, 3);
  }, [suggestionBase]);

  const relatedThreads = useMemo(() => {
    if (!thread || suggestionBase.length === 0) {
      return [];
    }
    const categoryKey = thread.category?.id ?? thread.category;
    if (!categoryKey) {
      return [];
    }
    return suggestionBase
      .filter((item) => (item.category?.id ?? item.category) === categoryKey)
      .slice(0, 3);
  }, [suggestionBase, thread]);

  const moreFromAuthorThreads = useMemo(() => {
    if (!thread?.author?.id) {
      return [];
    }
    return suggestionBase
      .filter((item) => (item.author?.id ?? item.author_id) === thread.author.id)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 3);
  }, [suggestionBase, thread?.author?.id]);

  const handleLike = async () => {
    if (!user) {
      setShowLoginPrompt("like");
      return;
    }
    if (thread && !thread.is_approved) {
      message.warning(
        "This thread is pending approval. Interactions are disabled."
      );
      return;
    }
    if (!slug) {
      return;
    }

    const previousLiked = liked;
    const previousLikes = displayedLikes;
    const nextLiked = !previousLiked;

    setLiked(nextLiked);
    setLocalLikes((prev) => {
      const base = prev ?? threadLikes;
      const delta = nextLiked ? 1 : -1;
      const next = base + delta;
      return next < 0 ? 0 : next;
    });

    try {
      await communityAPI.posts.toggleLike(slug);
      refreshThreadData().catch(() => { });
    } catch (err) {
      console.error("Failed to toggle like", err);
      message.error("Unable to update reaction");
      setLiked(previousLiked);
      setLocalLikes(previousLikes);
    }
  };

  const handleBookmark = async () => {
    if (!user) {
      setShowLoginPrompt("bookmark");
      return;
    }
    if (thread && !thread.is_approved) {
      message.warning(
        "This thread is pending approval. Interactions are disabled."
      );
      return;
    }
    if (!thread?.id) {
      return;
    }
    try {
      const res = await communityAPI.progress.toggleBookmark(thread.id);
      const next = Boolean(res?.data?.bookmarked ?? res?.bookmarked);
      setBookmarked(next);
      message.success(next ? "Bookmarked!" : "Bookmark removed");
      refreshThreadData().catch(() => { });
    } catch (err) {
      console.error("Error toggling bookmark:", err);
      message.error("Error toggling bookmark");
    }
  };

  const handleAuthorFollowToggle = async () => {
    if (!authorUsername) {
      return;
    }
    if (!user) {
      setShowLoginPrompt("follow");
      return;
    }
    if (isThreadAuthor) {
      message.info("You cannot follow yourself.");
      return;
    }
    if (authorFollowLoading) {
      return;
    }

    const previousState = authorFollowMeta;
    const optimisticFollowing = !previousState.isFollowing;

    setAuthorFollowMeta({
      isFollowing: optimisticFollowing,
      followers: Math.max(
        0,
        previousState.followers + (optimisticFollowing ? 1 : -1)
      ),
    });
    setAuthorFollowLoading(true);

    const action = previousState.isFollowing
      ? communityAPI.users.unfollow
      : communityAPI.users.follow;

    try {
      const { data } = await action(authorUsername);
      syncAuthorFollowMeta({
        followers_count:
          typeof data?.followers_count === "number"
            ? data.followers_count
            : typeof data?.followers === "number"
              ? data.followers
              : undefined,
        is_following:
          typeof data?.is_following === "boolean"
            ? data.is_following
            : undefined,
      });
      message.success(
        previousState.isFollowing
          ? "You are no longer following this member."
          : "You are now following this member."
      );
    } catch (err) {
      console.error("Failed to toggle follow", err);
      setAuthorFollowMeta({
        isFollowing: previousState.isFollowing,
        followers: previousState.followers,
      });
      message.error(
        previousState.isFollowing
          ? "Unable to unfollow right now."
          : "Unable to follow this member right now."
      );
    } finally {
      setAuthorFollowLoading(false);
    }
  };

  const handlePostReply = async () => {
    if (replyLoading) return;
    if (!user) {
      setShowLoginPrompt("comment");
      return;
    }
    if (thread && !thread.is_approved) {
      message.warning(
        "This thread is pending approval. Comments are disabled."
      );
      return;
    }
    if (!postReplyText.trim()) return;
    try {
      setReplyLoading(true);
      await createComment({
        content: postReplyText,
        parent: null,
      });
      setPostReplyText("");
      refreshThreadData().catch(() => { });
    } catch (err) {
      console.error("Failed to post reply", err);
    } finally {
      setReplyLoading(false);
    }
  };

  const handleCommentReply = async () => {
    if (replyLoading) return;
    if (!user) {
      setShowLoginPrompt("reply");
      return;
    }
    if (thread && !thread.is_approved) {
      message.warning(
        "This thread is pending approval. Comments are disabled."
      );
      return;
    }
    if (!replyingTo) return;
    if (!commentReplyText.trim()) return;

    try {
      setReplyLoading(true);
      await createComment({
        content: commentReplyText,
        parent: replyingTo || null,
      });
      setCommentReplyText("");
      setReplyingTo(null);
      refreshThreadData().catch(() => { });
    } catch (err) {
      console.error("Failed to post reply", err);
    } finally {
      setReplyLoading(false);
    }
  };

  const handleCommentLike = async (commentId) => {
    if (!user) {
      setShowLoginPrompt("like");
      return;
    }
    if (thread && !thread.is_approved) {
      message.warning(
        "This thread is pending approval. Interactions are disabled."
      );
      return;
    }
    try {
      await toggleCommentLike(commentId);
      refreshThreadData().catch(() => { });
    } catch (err) {
      console.error("Failed to like comment", err);
    }
  };

  const beginCommentEdit = (comment) => {
    if (!user) {
      setShowLoginPrompt("edit");
      return;
    }
    if (thread && !thread.is_approved) {
      message.warning("This thread is pending approval. Editing is disabled.");
      return;
    }
    setEditingCommentId(comment.id);
    setEditContent(comment.content || "");
    setReplyingTo(null);
    setCommentReplyText("");
  };

  const cancelCommentEdit = () => {
    setEditingCommentId(null);
    setEditContent("");
  };

  const handleCommentUpdate = async (commentId) => {
    if (thread && !thread.is_approved) {
      message.warning("This thread is pending approval. Editing is disabled.");
      return;
    }
    if (!editContent.trim()) {
      message.error("Comment cannot be empty");
      return;
    }
    try {
      await updateComment(commentId, { content: editContent });
      cancelCommentEdit();
      refreshThreadData().catch(() => { });
    } catch (err) {
      console.error("Failed to update comment", err);
    }
  };

  const handleCommentDelete = async (commentId) => {
    if (thread && !thread.is_approved) {
      message.warning("This thread is pending approval. Deleting is disabled.");
      return;
    }
    try {
      await deleteComment(commentId);
      if (editingCommentId === commentId) {
        cancelCommentEdit();
      }
      if (replyingTo === commentId) {
        setReplyingTo(null);
        setCommentReplyText("");
      }
      refreshThreadData().catch(() => { });
    } catch (err) {
      console.error("Failed to delete comment", err);
    }
  };

  const confirmCommentDelete = (commentId) => {
    if (thread && !thread.is_approved) {
      message.warning("This thread is pending approval. Deleting is disabled.");
      return;
    }
    Modal.confirm({
      title: "Delete comment?",
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => handleCommentDelete(commentId),
    });
  };

  const openReportModal = (comment) => {
    if (!user) {
      setShowLoginPrompt("report");
      return;
    }
    if (thread && !thread.is_approved) {
      message.warning(
        "This thread is pending approval. Reporting is disabled."
      );
      return;
    }
    if (comment.author?.id === user?.id) {
      message.warning("You cannot report your own comment");
      return;
    }
    setReportingComment(comment);
    setReportModalOpen(true);
  };

  const closeReportModal = () => {
    setReportModalOpen(false);
    setReportingComment(null);
  };

  const submitReport = async ({ reason, description }) => {
    if (!reportingComment) {
      message.error("No comment selected");
      return;
    }
    setSubmittingReport(true);
    try {
      await communityAPI.comments.report(reportingComment.id, {
        reason,
        description,
      });
      message.success("Report submitted for review.");
      closeReportModal();
    } catch (err) {
      console.error("Failed to submit report", err);
      const data = err.response?.data;
      let detail = err.response?.data?.detail;
      if (!detail && data) {
        const firstKey = Object.keys(data)[0];
        const value = data[firstKey];
        detail = Array.isArray(value) ? value[0] : value;
      }
      message.error(detail || "Unable to submit report");
    } finally {
      setSubmittingReport(false);
    }
  };

  const buildThreadLink = (entry) => {
    if (!entry) return "/forum";
    return entry.slug ? `/forum/${entry.slug}` : `/forum/${entry.id}`;
  };

  const extractTextExcerpt = (htmlContent, maxLength = 150) => {
    if (!htmlContent) return "";
    const doc = new DOMParser().parseFromString(htmlContent, "text/html");
    const text = (doc.body.textContent || "").replace(/\s+/g, " ").trim();
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const indentClasses = [
    "",
    "ml-8",
    "ml-12",
    "ml-16",
    "ml-20",
    "ml-24",
    "ml-28",
    "ml-32",
  ];

  const renderComment = (comment, depth = 0) => {
    const indentClass =
      indentClasses[Math.min(depth, indentClasses.length - 1)];
    const cardClasses =
      depth === 0
        ? "bg-white dark:bg-[#161616] rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700"
        : "bg-gray-50 dark:bg-[#161616]/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700";
    const avatarSize = depth === 0 ? 12 : 10;
    const timestampClass = depth === 0 ? "text-sm" : "text-xs";
    const fontSizeClass = depth === 0 ? "" : "text-sm";
    const reactionTextClass = depth === 0 ? "text-sm" : "text-xs";
    const reactionIconClass = depth === 0 ? "w-4 h-4" : "w-3 h-3";
    const readMoreTextClass = depth === 0 ? "text-sm" : "text-xs";
    const replyFormIndentClass =
      indentClasses[Math.min(depth + 1, indentClasses.length - 1)];
    const rawContent = comment.content || "";
    const isEditing = editingCommentId === comment.id;
    const previewLimit = depth === 0 ? 300 : 200;
    const needsClamp = !isEditing && rawContent.length > previewLimit;
    const isExpanded = !!expandedComments[comment.id];
    const displayedContent =
      !needsClamp || isExpanded
        ? rawContent
        : `${rawContent.substring(0, previewLimit)}...`;
    const childReplies = Array.isArray(comment.replies) ? comment.replies : [];
    const totalReplies = childReplies.length;
    const baseVisibleReplies = depth === 0 ? 3 : 2;
    const loadIncrement = depth === 0 ? 5 : 3;
    const currentVisibleCount =
      visibleReplyCounts[comment.id] ?? baseVisibleReplies;
    const effectiveVisibleCount = Math.min(totalReplies, currentVisibleCount);
    const visibleReplies = childReplies.slice(0, effectiveVisibleCount);
    const hiddenCount = totalReplies - visibleReplies.length;
    const nextBatchCount =
      hiddenCount > 0 ? Math.min(loadIncrement, hiddenCount) : 0;
    const hiddenLabel = nextBatchCount === 1 ? "reply" : "replies";
    const replyIndentClass =
      indentClasses[Math.min(depth + 1, indentClasses.length - 1)];
    const canCollapseReplies =
      totalReplies > baseVisibleReplies &&
      effectiveVisibleCount > baseVisibleReplies;
    const canManage = Boolean(user && comment.author?.id === user.id);
    const canReport = Boolean(user && comment.author?.id !== user.id);

    return (
      <div
        key={comment.id}
        className={`${depth > 0 ? indentClass : ""} space-y-3`}
      >
        <div className={cardClasses}>
          <div className="flex items-start gap-4">
            <Avatar
              name={comment.author?.username || "Anonymous"}
              size={avatarSize}
              src={comment.author?.avatar_url || comment.author?.avatar}
            />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <div>
                  {comment.author?.username ? (
                    <Link
                      to={`/profile/${comment.author.username}`}
                      className={`font-semibold text-gray-900 dark:text-white ${fontSizeClass} hover:text-primary dark:hover:text-primary/80 transition-colors inline-flex items-center gap-1.5`}
                    >
                      {comment.author.name || comment.author.username}
                      {(comment.author.is_trusted || comment.author.status?.is_trusted) && (
                        <img
                          src={verifiedBadge}
                          alt="Verified"
                          className="w-3.5 h-3.5 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                        />
                      )}
                    </Link>
                  ) : (
                    <div
                      className={`font-semibold text-gray-900 dark:text-white ${fontSizeClass}`}
                    >
                      Anonymous
                    </div>
                  )}
                  <div
                    className={`${timestampClass} text-gray-500 dark:text-gray-400`}
                  >
                    {formatDate(comment.created_at)}
                  </div>
                </div>
              </div>
              {isEditing ? (
                <div className="space-y-3">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-[#161616] text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    rows={3}
                    placeholder="Update your comment"
                  />
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCommentUpdate(comment.id)}
                        className="px-3 py-1 text-xs rounded-lg bg-primary hover:bg-[#229bb8] text-white"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelCommentEdit}
                        className="px-3 py-1 text-xs rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                    {canManage && (
                      <button
                        onClick={() => confirmCommentDelete(comment.id)}
                        className="px-3 py-1 text-xs rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <p
                    className={`text-gray-700 dark:text-gray-300 leading-relaxed mb-3 break-words ${fontSizeClass}`}
                  >
                    {displayedContent}
                  </p>
                  {needsClamp && (
                    <button
                      onClick={() =>
                        setExpandedComments((prev) => ({
                          ...prev,
                          [comment.id]: !prev[comment.id],
                        }))
                      }
                      className={`text-primary dark:text-primary/80 ${readMoreTextClass} font-medium hover:underline mb-3`}
                    >
                      {isExpanded ? "Show less" : "Read more"}
                    </button>
                  )}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => {
                        if (!thread?.is_approved) {
                          message.warning(
                            "This thread is pending approval. Comments are disabled."
                          );
                          return;
                        }
                        handleCommentLike(comment.id);
                      }}
                      disabled={!thread?.is_approved}
                      className={`flex items-center gap-1 ${reactionTextClass} ${comment.user_has_liked
                        ? "text-red-600 dark:text-red-400"
                        : "text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        } transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <svg
                        className={`${reactionIconClass} ${comment.user_has_liked ? "fill-current" : ""
                          }`}
                        fill={comment.user_has_liked ? "currentColor" : "none"}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                        />
                      </svg>
                      {formatNumber(comment.likes_count || 0)}
                    </button>
                    <button
                      onClick={() => {
                        if (!user) {
                          setShowLoginPrompt("reply");
                          return;
                        }
                        if (!thread?.is_approved) {
                          message.warning(
                            "This thread is pending approval. Comments are disabled."
                          );
                          return;
                        }
                        setEditingCommentId(null);
                        setEditContent("");
                        setReplyingTo(
                          replyingTo === comment.id ? null : comment.id
                        );
                        setCommentReplyText("");
                      }}
                      disabled={!user || !thread?.is_approved}
                      className={`text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${reactionTextClass}`}
                    >
                      Reply
                    </button>
                    {canReport && (
                      <ReportButton
                        onClick={() => openReportModal(comment)}
                        disabled={
                          !user ||
                          comment.author?.id === user?.id ||
                          !thread?.is_approved
                        }
                        className={reactionTextClass}
                      />
                    )}
                    {canManage && (
                      <>
                        <button
                          onClick={() => beginCommentEdit(comment)}
                          disabled={!thread?.is_approved}
                          className={`${reactionTextClass} text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => confirmCommentDelete(comment.id)}
                          disabled={!thread?.is_approved}
                          className={`${reactionTextClass} text-red-500 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {replyingTo === comment.id && (
          <div
            className={`${replyFormIndentClass} p-4 bg-white dark:bg-[#161616] rounded-xl border border-gray-200 dark:border-gray-700`}
          >
            <textarea
              value={commentReplyText}
              onChange={(e) => setCommentReplyText(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-[#161616] text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
              rows={3}
              placeholder={`Reply to ${comment.author?.username || "this comment"
                }...`}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => {
                  setReplyingTo(null);
                  setCommentReplyText("");
                }}
                disabled={replyLoading}
                className="px-3 py-1 text-sm rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCommentReply();
                }}
                disabled={replyLoading}
                className="px-4 py-1 text-sm rounded-lg bg-primary hover:bg-[#229bb8] text-white font-medium transition-colors duration-200 disabled:opacity-75 disabled:cursor-not-allowed"
              >
                {replyLoading ? (
                  <InlineSpinner
                    tone="slate"
                    message="Posting reply..."
                    messageClassName="text-white"
                  />
                ) : (
                  "Reply"
                )}
              </button>
            </div>
          </div>
        )}

        {childReplies.length > 0 && (
          <div className="space-y-4">
            {visibleReplies.map((child) => renderComment(child, depth + 1))}
            {hiddenCount > 0 && (
              <button
                onClick={() => {
                  setVisibleReplyCounts((prev) => {
                    const current = prev[comment.id] ?? baseVisibleReplies;
                    const next = Math.min(
                      totalReplies,
                      current + loadIncrement
                    );
                    return { ...prev, [comment.id]: next };
                  });
                }}
                className={`${replyIndentClass} text-primary dark:text-primary/80 ${depth === 0 ? "text-sm" : "text-xs"
                  } font-medium hover:underline`}
              >
                Show {formatNumber(nextBatchCount)} more {hiddenLabel}
              </button>
            )}
            {canCollapseReplies && (
              <button
                onClick={() => {
                  setVisibleReplyCounts((prev) => {
                    if (!(comment.id in prev)) {
                      return prev;
                    }
                    const nextState = { ...prev };
                    delete nextState[comment.id];
                    return nextState;
                  });
                }}
                className={`${replyIndentClass} text-primary dark:text-primary/80 ${depth === 0 ? "text-sm" : "text-xs"
                  } font-medium hover:underline`}
              >
                Show fewer replies
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  if (threadLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-16 sm:pt-24 blog-body">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <FullscreenSpinner message="Loading discussion..." tone="slate" />
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16 sm:pt-24 blog-body">
        <div className="text-red-600 dark:text-red-400 text-xl">
          Thread not found
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-16 sm:pt-24 blog-body">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@400;500;600&display=swap');
        .blog-title { font-family: 'Crimson Pro', Georgia, serif; }
        .blog-body { font-family: 'DM Sans', sans-serif; }
        .prose-blog { 
          font-family: 'DM Sans', sans-serif; 
          font-size: 1.0625rem; 
          line-height: 1.8; 
          color: #2d2d2d;
          word-break: normal;
          overflow-wrap: break-word !important; /* Allow long words/URLs to wrap safely */
          word-wrap: break-word !important;
          hyphens: none !important;
          -webkit-hyphens: none !important;
        }
        .prose-blog * {
          white-space: normal !important; /* Force standard text flow if editor applied nowrap */
          hyphens: none !important;
          -webkit-hyphens: none !important;
        }
        .dark .prose-blog { color: #d1d5db; }
        .prose-blog h1,.prose-blog h2,.prose-blog h3,.prose-blog h4 {
          font-family: 'Crimson Pro', Georgia, serif;
          font-weight: 600;
          margin-top: 1.6em;
          margin-bottom: 0.6em;
          line-height: 1.3;
          letter-spacing: -0.01em;
        }
        .prose-blog p { margin-bottom: 1.35em; }
        .prose-blog a {
          color: #2Ba8CB;
          text-decoration: underline;
          text-underline-offset: 3px;
          text-decoration-color: rgba(43, 168, 203, 0.35);
          transition: color 0.2s ease, text-decoration-color 0.2s ease;
        }
        .prose-blog a:hover { color: #1e8fa8; text-decoration-color: #1e8fa8; }
        .prose-blog ul, .prose-blog ol { padding-left: 0.25rem; margin-top: 0.75em; margin-bottom: 0.75em; }
        .prose-blog ul { list-style: none; }
        .prose-blog ul > li, .prose-blog ol > li {
          position: relative; padding-left: 1.75em; margin-bottom: 0.5em; line-height: 1.7;
        }
        .prose-blog ul > li::before {
          content: ""; position: absolute; left: 0.25em; top: 0.6em;
          width: 8px; height: 8px; border-radius: 50%;
          background: linear-gradient(135deg, #2Ba8CB 0%, #1e8fa8 100%);
          box-shadow: 0 0 6px rgba(43, 168, 203, 0.35);
        }
        .prose-blog ul ul > li::before { background: transparent; border: 2px solid #2Ba8CB; box-shadow: none; width: 7px; height: 7px; }
        .prose-blog ul ul ul > li::before { border: none; border-radius: 0; background: #2Ba8CB; width: 10px; height: 2px; top: 0.75em; }
        .prose-blog ol { list-style: none; counter-reset: forum-ol; }
        .prose-blog ol > li { counter-increment: forum-ol; }
        .prose-blog ol > li::before {
          content: counter(forum-ol) "."; position: absolute; left: 0; top: 0;
          font-weight: 700; font-size: 0.9em; color: #2Ba8CB;
          font-variant-numeric: tabular-nums; min-width: 1.5em;
        }
        .prose-blog ul ul, .prose-blog ol ol, .prose-blog ul ol, .prose-blog ol ul { margin-top: 0.35em; margin-bottom: 0.35em; }
        .dark .prose-blog ul > li::before { background: linear-gradient(135deg, #38bdf8 0%, #2Ba8CB 100%); box-shadow: 0 0 6px rgba(56, 189, 248, 0.3); }
        .dark .prose-blog ul ul > li::before { background: transparent; border-color: #38bdf8; box-shadow: none; }
        .dark .prose-blog ul ul ul > li::before { border: none; background: #38bdf8; }
        .dark .prose-blog ol > li::before { color: #38bdf8; }
        .prose-blog blockquote {
          border-left: 3px solid #2Ba8CB; padding: 0.75rem 1.25rem; margin: 1.5em 0;
          background: rgba(43, 168, 203, 0.04); border-radius: 0 8px 8px 0; color: #6b7280; font-style: italic;
        }
        .dark .prose-blog blockquote { background: rgba(43, 168, 203, 0.08); color: #a1a1aa; }
        .prose-blog code { background: #f0faf9; color: #2Ba8CB; padding: 0.15em 0.45em; border-radius: 5px; font-size: 0.88em; font-weight: 500; }
        .dark .prose-blog code { background: rgba(43, 168, 203, 0.15); color: #5cc9e0; }
        .prose-blog pre {
          background: #1a1a2e; color: #e2e8f0; padding: 1.25rem 1.5rem; border-radius: 12px;
          overflow-x: auto; margin: 1.5em 0; font-size: 0.88em; line-height: 1.7;
          border: 1px solid rgba(255, 255, 255, 0.06);
        }
        .prose-blog pre code { background: transparent; color: inherit; padding: 0; border-radius: 0; font-size: inherit; font-weight: 400; }
        .prose-blog table { width: 100%; border-collapse: collapse; margin: 1.5em 0; font-size: 0.925em; }
        .prose-blog thead th {
          background: #f8fafc; font-weight: 600; text-align: left; padding: 0.65em 1em;
          border-bottom: 2px solid #e2e8f0; color: #374151; font-size: 0.85em;
          text-transform: uppercase; letter-spacing: 0.04em;
        }
        .dark .prose-blog thead th { background: rgba(255, 255, 255, 0.04); border-bottom-color: #374151; color: #d1d5db; }
        .prose-blog tbody td { padding: 0.6em 1em; border-bottom: 1px solid #f1f5f9; }
        .dark .prose-blog tbody td { border-bottom-color: #1f2937; }
        .prose-blog tbody tr:hover { background: rgba(43, 168, 203, 0.03); }
        .dark .prose-blog tbody tr:hover { background: rgba(43, 168, 203, 0.06); }
        .prose-blog img { border-radius: 12px; margin: 1.5em 0; }
        .prose-blog hr {
          border: none; height: 1px; margin: 2em 0;
          background: linear-gradient(90deg, transparent, #d1d5db 20%, #d1d5db 80%, transparent);
        }
        .dark .prose-blog hr { background: linear-gradient(90deg, transparent, #374151 20%, #374151 80%, transparent); }
        .prose-blog mark { background: linear-gradient(120deg, #fff3b0 0%, #ffe066 100%); color: inherit; padding: 0.1em 0.2em; border-radius: 3px; }
        .dark .prose-blog mark { background: linear-gradient(120deg, rgba(255, 243, 176, 0.25) 0%, rgba(255, 224, 102, 0.25) 100%); }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10 items-start">
          <div className="min-w-0">
            <Link
              to={
                fromReports
                  ? "/admin/reports"
                  : fromAdmin
                    ? adminBackTo
                    : "/forum"
              }
              className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-primary font-medium mt-3 mb-3 transition-colors duration-200 group"
            >
              <svg
                className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {fromReports
                ? "Back to Reports"
                : fromAdmin
                  ? "Back to admin"
                  : "Back to Forum"}
            </Link>

            <div className="bg-white dark:bg-[#161616] rounded-3xl shadow-2xl overflow-hidden mb-8">
              <div className="p-8 lg:p-12">
                <div className="flex flex-wrap items-start gap-6 mb-6">
                  <div className="flex items-start gap-6 flex-1 min-w-[260px]">
                    <Avatar
                      name={thread.author?.username || "Anonymous"}
                      size={16}
                      src={thread.author?.avatar_url || thread.author?.avatar}
                    />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        {thread.category && (
                          <Badge color="yellow">
                            {thread.category?.name || thread.category}
                          </Badge>
                        )}
                        {statusMeta && (
                          <Badge
                            color={statusMeta.color}
                            className="text-xs whitespace-nowrap"
                          >
                            {statusMeta.label}
                          </Badge>
                        )}
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(thread.created_at)}
                        </span>
                      </div>
                      <h1 className="blog-title text-4xl lg:text-5xl font-bold text-gray-950 dark:text-white leading-tight mb-4">
                        {thread.title}
                      </h1>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Started by{" "}
                        {thread.author?.username ? (
                          <Link
                            to={`/profile/${thread.author.username}`}
                            className="font-medium text-gray-900 dark:text-white hover:text-primary dark:hover:text-primary/80 transition-colors inline-flex items-center gap-1.5"
                          >
                            {thread.author.name || thread.author.username}
                            {(thread.author.is_trusted || thread.author.status?.is_trusted) && (
                              <img
                                src={verifiedBadge}
                                alt="Verified"
                                className="w-4 h-4 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                              />
                            )}
                          </Link>
                        ) : (
                          <span className="font-medium text-gray-900 dark:text-white">
                            Anonymous
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {statusMeta && thread.review_feedback?.message && (
                  <div className="mb-6">
                    <ReviewFeedbackNotice feedback={thread.review_feedback} />
                  </div>
                )}

                <div
                  className="prose-blog prose prose-lg dark:prose-invert max-w-none mb-6"
                  dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                />

                <div className="flex flex-wrap items-center justify-between gap-y-4 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <button
                      onClick={handleLike}
                      disabled={!thread?.is_approved}
                      className={`action-pill ${liked
                        ? "bg-red-50 dark:bg-red-900/20 text-red-500"
                        : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-red-500"
                        } disabled:opacity-50`}
                    >
                      <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} />
                      {formatNumber(displayedLikes)}
                    </button>
                    <button
                      onClick={handleBookmark}
                      disabled={!thread?.is_approved}
                      className={`action-pill ${bookmarked
                        ? "bg-amber-50 dark:bg-amber-900/20 text-amber-500"
                        : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-amber-500"
                        } disabled:opacity-50`}
                    >
                      <svg className="w-4 h-4" fill={bookmarked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
                      <span className="hidden sm:inline">Bookmark</span>
                    </button>
                    <button
                      onClick={() => setShareModalOpen(true)}
                      disabled={!thread?.is_approved}
                      className="action-pill text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" /></svg>
                      <span className="hidden sm:inline">Share</span>
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500 font-medium action-pill border-none bg-transparent cursor-default">
                    <Eye className="w-4 h-4" />
                    <span className="text-xs">{formatNumber(thread.views || 0)}</span>
                  </div>
                </div>              </div>
              <ReportModal
                isOpen={reportModalOpen}
                onClose={closeReportModal}
                onSubmit={submitReport}
                submitting={submittingReport}
                comment={reportingComment}
              />
              <ShareModal
                isOpen={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                title={thread?.title || ""}
                url={window.location.href}
                description={extractTextExcerpt(thread?.content)}
              />
            </div>

            {resolvedAuthor && (
              <section className="mb-10 space-y-8">
                <AuthorSpotlight
                  authorName={authorDisplayName}
                  username={authorUsername}
                  avatar={authorAvatar}
                  isTrusted={resolvedAuthor?.is_trusted || resolvedAuthor?.status?.is_trusted}
                  headline={authorHeadline}
                  bio={authorBio}
                  skills={authorSkills}
                  followerCount={authorFollowMeta.followers}
                  followerLabel={threadFollowerLabel}
                  followingCount={authorFollowingCount}
                  joinedDate={authorJoinedDate}
                  showFollowButton={showAuthorFollowCta}
                  onFollowToggle={handleAuthorFollowToggle}
                  followLoading={authorFollowLoading}
                  isFollowing={authorFollowMeta.isFollowing}
                  profileLink={authorProfileLink}
                  selfNotice="You created this thread"
                />
                <MoreFromAuthor
                  authorName={authorDisplayName}
                  items={moreFromAuthorThreads}
                  loading={forumCollectionLoading}
                  profileLink={authorProfileLink}
                  hideWhenEmpty
                  showMedia={false}
                  emptyMessage={`No additional threads from ${authorDisplayName} yet.`}
                  loadingMessage="Gathering threads..."
                  buildItemLink={buildThreadLink}
                  getExcerpt={(item) => extractTextExcerpt(item.content, 200)}
                  getDateLabel={(item) =>
                    item?.created_at ? formatDate(item.created_at) : "Recently"
                  }
                  renderStats={(item) => [
                    <span key="replies" className="flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> {formatNumber(item.comments_count ?? item.comments_total ?? 0)}</span>,
                    <span key="likes" className="flex items-center gap-1.5"><Heart className="w-3.5 h-3.5" /> {formatNumber(item.likes_total ?? item.likes_count ?? 0)}</span>,
                    <span key="views" className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> {formatNumber(item.views || 0)}</span>,
                  ]}
                />
              </section>
            )}

            {!user && (
              <div className="mb-8">
                <LoginPrompt action="comment" inline />
              </div>
            )}

            {user && thread?.is_approved && (
              <div className="bg-white dark:bg-[#161616] rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Add Your Reply
                </h3>
                <div className="space-y-4">
                  <textarea
                value={postReplyText}
                onChange={(e) => setPostReplyText(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-[#161616] text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    rows={4}
                    placeholder="Share your thoughts..."
                  />
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                    setPostReplyText("");
                      }}
                      disabled={replyLoading}
                      className="px-6 py-3 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePostReply();
                      }}
                      disabled={replyLoading}
                      className="px-6 py-3 rounded-xl bg-primary hover:bg-[#229bb8] text-white font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl disabled:opacity-80 disabled:cursor-not-allowed"
                    >
                      {replyLoading ? (
                        <InlineSpinner
                          tone="slate"
                          message="Posting reply..."
                          messageClassName="text-white"
                        />
                      ) : (
                        "Post Reply"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!thread.is_approved && (
              <div className="mb-6 text-yellow-700 bg-yellow-50 dark:text-yellow-200 dark:bg-yellow-900/30 px-4 py-3 rounded-lg">
                {reviewStatus === "requires_revision"
                  ? "This thread needs author revision before it can return to the community. Interactions stay disabled until it is approved."
                  : "This thread is awaiting moderator approval. Commenting and reactions are disabled for now."}
              </div>
            )}

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Replies ({formatNumber(comments.length)})
              </h2>

              {commentsLoading ? (
                <div className="py-10 flex justify-center">
                  <InlineSpinner tone="slate" message="Loading replies..." />
                </div>
              ) : comments.length > 0 ? (
                comments
                  .slice(0, repliesToShow)
                  .map((reply) => renderComment(reply))
              ) : (
                <div className="text-center py-10 text-gray-600 dark:text-gray-400">
                  No replies yet. Be the first to contribute!
                </div>
              )}

              {comments.length > repliesToShow && (
                <button
                  onClick={() => setRepliesToShow((prev) => prev + 5)}
                  className="w-full py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl text-gray-900 dark:text-white font-semibold transition-all mt-6"
                >
                  Load more replies (
                  {formatNumber(Math.max(comments.length - repliesToShow, 0))}{" "}
                  remaining)
                </button>
              )}
            </div>
          </div>

          <aside className="lg:col-span-1 lg:sticky lg:top-24 lg:h-max">
            <div className="space-y-6">

              {/* Featured Threads */}
              <div className="bg-white dark:bg-[#161616] rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-800">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Featured Threads</h3>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {forumCollectionLoading ? (
                    <div className="px-5 py-6 flex justify-center">
                      <InlineSpinner tone="slate" message="Loading threads..." />
                    </div>
                  ) : featuredThreads.length > 0 ? (
                    featuredThreads.map((item) => (
                      <Link
                        key={item.id}
                        to={buildThreadLink(item)}
                        className="block px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                      >
                        <h4 className="font-medium text-sm text-gray-900 dark:text-white group-hover:text-primary transition-colors line-clamp-2 mb-1">
                          {truncateText(item.title, 60)}
                        </h4>
                        <div className="flex flex-wrap items-center gap-2.5 text-xs text-gray-400 dark:text-gray-500">
                          <span className="font-medium text-gray-500 dark:text-gray-400">{item.category?.name || "General"}</span>
                          <div className="flex items-center gap-2.5">
                            <span className="flex items-center gap-1" title="Replies"><MessageCircle className="w-3.5 h-3.5" /> {formatNumber(item.comments_count ?? item.comments_total ?? 0)}</span>
                            <span className="flex items-center gap-1" title="Views"><Eye className="w-3.5 h-3.5" /> {formatNumber(item.views || 0)}</span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center">
                      No featured threads available.
                    </div>
                  )}
                </div>
              </div>

              {/* Related Threads */}
              <div className="bg-white dark:bg-[#161616] rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-800">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Related Threads</h3>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {forumCollectionLoading ? (
                    <div className="px-5 py-6 flex justify-center">
                      <InlineSpinner tone="slate" message="Loading threads..." />
                    </div>
                  ) : relatedThreads.length > 0 ? (
                    relatedThreads.map((item) => (
                      <Link
                        key={item.id}
                        to={buildThreadLink(item)}
                        className="block px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                      >
                        <h4 className="font-medium text-sm text-gray-900 dark:text-white group-hover:text-primary transition-colors line-clamp-2 mb-1">
                          {truncateText(item.title, 60)}
                        </h4>
                        <div className="flex flex-wrap items-center gap-2.5 text-xs text-gray-400 dark:text-gray-500">
                          <span className="font-medium text-gray-500 dark:text-gray-400">{item.category?.name || "General"}</span>
                          <div className="flex items-center gap-2.5">
                            <span className="flex items-center gap-1" title="Views"><Eye className="w-3.5 h-3.5" /> {formatNumber(item.views || 0)}</span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center">
                      No related threads available.
                    </div>
                  )}
                </div>
              </div>

            </div>
          </aside>
        </div>
      </div>
      {showLoginPrompt && (
        <LoginPrompt
          action={showLoginPrompt}
          onClose={() => setShowLoginPrompt(null)}
        />
      )}
    </div>
  );
}
